import { Link, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getMoviesByCategory, getCategoryBySlug, categories } from '@/mocks/movies';
import { siteConfig } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'popular'>('latest');

  const category = getCategoryBySlug(slug || '');
  const allMovies = getMoviesByCategory(slug || '');

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    allMovies.forEach(m => m.tags.forEach(t => tagSet.add(t)));
    return ['全部', ...Array.from(tagSet)];
  }, [allMovies]);

  const filteredMovies = useMemo(() => {
    let result = activeTag === '全部' ? allMovies : allMovies.filter(m => m.tags.includes(activeTag));
    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => b.views - a.views);
    }
    return result;
  }, [allMovies, activeTag, sortBy]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">分类不存在</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-2 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryUrl = `${siteConfig.url}/category/${category.slug}`;
  const categoryTitle = `${category.name} - 小辣椒影院免费在线观看`;
  const categoryDesc = `小辣椒影院${category.name}频道：${category.description}。高清${category.name}免费在线观看，画质清晰不卡顿，秒开流畅播放。`;
  const categoryKeywords = `小辣椒影院,${category.name},${category.name}在线观看,${category.name}免费,高清${category.name}`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={categoryTitle}
        description={categoryDesc}
        keywords={categoryKeywords}
        canonicalUrl={categoryUrl}
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: categoryTitle,
            description: categoryDesc,
            url: categoryUrl,
            isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '小辣椒影院', item: siteConfig.url },
              { '@type': 'ListItem', position: 2, name: category.name, item: categoryUrl },
            ],
          },
        ]}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto pt-6 pb-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium">{category.name}</span>
            </nav>
          </div>
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className={`${category.icon} text-primary-500 text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
                </div>
                <p className="text-sm text-foreground-500 ml-13">{category.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">共 <strong className="text-primary-500">{filteredMovies.length}</strong> 部</span>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Filter & Sort */}
        <section className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-background-100 rounded-xl p-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-foreground-500 mr-1 whitespace-nowrap">类型：</span>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all duration-200 ${
                      activeTag === tag
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                  { key: 'popular', label: '最热' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key as typeof sortBy)}
                    className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all duration-200 ${
                      sortBy === opt.key
                        ? 'bg-accent-100 text-accent-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto mb-3"></i>
                <p className="text-foreground-500">该分类下暂无内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie, index) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group animate-scale-in"
                    style={{ animationDelay: `${index * 0.04}s` }}
                    data-product-shop
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={movie.posterImage}
                        alt={movie.title}
                        title={`${movie.title} - 小辣椒影院${category.name}免费在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-3">
                        <span className="text-white text-xs bg-primary-500/90 px-3 py-1 rounded-full whitespace-nowrap">
                          查看详情
                        </span>
                      </div>
                      <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap">
                        {movie.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {movie.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-foreground-500 mt-0.5">
                      <span className="bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded whitespace-nowrap">{movie.tags[0]}</span>
                      <span>{movie.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}